import {
  QUESTION_TYPE_MULTIPLE_CHOICE,
  QUESTION_TYPE_SHORT_ANSWER,
  createMcqOptionId,
  validateMcqQuestion,
} from "./mcqQuestion";

export const CSV_MCQ_OPTION_COLUMNS = [
  "optionA", "optionB", "optionC", "optionD", "optionE", "optionF",
];

const text = (value) => typeof value === "string" ? value.trim() : "";
const normalizedOptionText = (value) => text(value).replace(/\s+/g, " ").toLocaleLowerCase();

const csvQuestionType = (value) => {
  const normalized = text(value).toLocaleLowerCase().replace(/[\s_-]+/g, "");
  if (!normalized || normalized === "shortanswer") return QUESTION_TYPE_SHORT_ANSWER;
  if (normalized === "multiplechoice") return QUESTION_TYPE_MULTIPLE_CHOICE;
  return null;
};

const textBoxSize = (value) => {
  const normalized = text(value).toLocaleLowerCase();
  if (normalized === "small" || normalized === "large") return normalized;
  return "medium";
};

const hasAuthoritativeMcqFields = (row) => Object.entries(row || {}).some(([key, value]) => {
  if (!text(value)) return false;
  const normalizedKey = key.toLocaleLowerCase().replace(/[\s_-]+/g, "");
  return normalizedKey === "correctoptionid" ||
    normalizedKey === "iscorrect" ||
    normalizedKey === "options" ||
    normalizedKey.includes("optionid") ||
    (normalizedKey.startsWith("option") && normalizedKey.endsWith("id"));
});

const commonErrors = (row) => {
  const errors = [];
  if (!text(row?.questionText)) errors.push("questionText is required");
  if (!text(row?.topic)) errors.push("topic is required");
  if (!text(row?.subject)) errors.push("subject is required");
  if (!text(row?.level)) errors.push("level is required");
  if (!text(row?.specCode)) errors.push("specCode is required");
  return errors;
};

const commonFields = (row) => ({
  questionLabel: typeof row?.questionLabel === "string" ? row.questionLabel : "",
  questionText: text(row?.questionText),
  topic: text(row?.topic),
  subject: text(row?.subject),
  level: text(row?.level),
  specCode: text(row?.specCode),
});

export const normalizeCsvQuestionRow = (row = {}) => {
  const errors = commonErrors(row);
  const questionType = csvQuestionType(row.questionType);

  if (!questionType) {
    errors.push(`questionType must be Short Answer or Multiple Choice (got "${text(row.questionType)}")`);
  }

  if (questionType === QUESTION_TYPE_SHORT_ANSWER) {
    if (!text(row.markSchemeAnswer)) errors.push("markSchemeAnswer is required");
    if (!row.maxMark) {
      errors.push("maxMark is required");
    } else {
      const parsedMark = parseInt(row.maxMark, 10);
      if (Number.isNaN(parsedMark) || parsedMark <= 0) errors.push("maxMark must be a positive number");
    }

    if (text(row.answerInputSize) && !["small", "medium", "large"].includes(text(row.answerInputSize).toLocaleLowerCase())) {
      errors.push(`answerInputSize must be one of: Small, Medium, Large (got "${row.answerInputSize}")`);
    }

    return {
      valid: errors.length === 0,
      errors,
      value: errors.length ? null : {
        ...commonFields(row),
        markSchemeAnswer: text(row.markSchemeAnswer),
        maxMark: parseInt(row.maxMark, 10),
        textBoxSize: textBoxSize(row.answerInputSize),
      },
      questionType: QUESTION_TYPE_SHORT_ANSWER,
    };
  }

  if (hasAuthoritativeMcqFields(row)) {
    errors.push("Option IDs, correctOptionId, options, and isCorrect are not accepted in CSV files.");
  }

  const populatedBeyondF = Object.entries(row).some(([key, value]) =>
    /^option[g-z]$/i.test(key.trim()) && Boolean(text(value))
  );
  if (populatedBeyondF) errors.push("Multiple-choice questions support no more than 6 options (A-F).");

  const optionTexts = CSV_MCQ_OPTION_COLUMNS.map((column) => text(row[column]));
  const populatedIndexes = optionTexts
    .map((optionText, index) => optionText ? index : -1)
    .filter((index) => index >= 0);
  const populatedCount = populatedIndexes.length;

  if (populatedCount < 2) {
    errors.push("Multiple-choice questions require at least 2 options.");
  }
  if (populatedCount > 0) {
    const lastPopulatedIndex = populatedIndexes[populatedIndexes.length - 1];
    const firstGap = optionTexts.slice(0, lastPopulatedIndex + 1).findIndex((optionText) => !optionText);
    if (firstGap >= 0) {
      errors.push(`Options must be contiguous from A; option ${String.fromCharCode(65 + firstGap)} is missing.`);
    }
  }

  const normalizedTexts = optionTexts.filter(Boolean).map(normalizedOptionText);
  if (new Set(normalizedTexts).size !== normalizedTexts.length) {
    errors.push("Duplicate option text is not allowed.");
  }

  const correctOption = text(row.correctOption).toLocaleUpperCase();
  if (!correctOption) {
    errors.push("correctOption is required for multiple-choice questions.");
  } else if (!/^[A-F]$/.test(correctOption)) {
    errors.push(`correctOption "${text(row.correctOption)}" must be a letter from A to F.`);
  } else {
    const correctIndex = correctOption.charCodeAt(0) - 65;
    if (!optionTexts[correctIndex]) {
      errors.push(`correctOption "${correctOption}" does not reference a populated option.`);
    }
  }

  if (!row.maxMark) {
    errors.push("maxMark is required");
  } else if (text(row.maxMark) !== "1") {
    errors.push("Multiple-choice questions must have maxMark exactly 1.");
  }

  if (errors.length) {
    return { valid: false, errors, value: null, questionType: QUESTION_TYPE_MULTIPLE_CHOICE };
  }

  const contiguousOptions = optionTexts.slice(0, populatedCount).map((optionText) => ({
    id: createMcqOptionId(),
    text: optionText,
  }));
  const correctIndex = correctOption.charCodeAt(0) - 65;
  const value = {
    ...commonFields(row),
    questionType: QUESTION_TYPE_MULTIPLE_CHOICE,
    options: contiguousOptions,
    correctOptionId: contiguousOptions[correctIndex].id,
    maxMark: 1,
  };
  const authoritativeValidation = validateMcqQuestion(value);
  if (!authoritativeValidation.isValid) {
    return {
      valid: false,
      errors: authoritativeValidation.errors,
      value: null,
      questionType: QUESTION_TYPE_MULTIPLE_CHOICE,
    };
  }

  return {
    valid: true,
    errors: [],
    value: { ...value, ...authoritativeValidation.value },
    questionType: QUESTION_TYPE_MULTIPLE_CHOICE,
  };
};

export const csvQuestionDocumentFields = (previewRow) => {
  if (previewRow?._valid && previewRow?._questionData) return previewRow._questionData;
  const normalized = normalizeCsvQuestionRow(previewRow);
  if (!normalized.valid) {
    const error = new Error(normalized.errors.join(" "));
    error.validationErrors = normalized.errors;
    throw error;
  }
  return normalized.value;
};
